import { useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";
import { Chart } from "highcharts";

export interface HalfChartDataItem {
  name: string;
  y: number;
}

export const useChartOptions = (
  chartColors: string[] | undefined,
  chartData: HalfChartDataItem[] | undefined,
  width?: number,
  height?: number,
  activeItem?: string,
  setActiveItem?: (item?: string) => void
): Highcharts.Options => {
  const theme = useTheme();

  const highlightSection = useCallback(
    (chart: Chart) =>
      chart.series[0]?.points.forEach((point) => {
        if (point.name === activeItem) {
          point.setState("hover");
        } else {
          point.setState("");
        }
      }),
    [activeItem]
  );

  return useMemo(() => {
    return {
      title: {
        text: "",
      },
      credits: {
        enabled: false,
      },
      chart: {
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        plotShadow: false,
        type: "pie",
        width,
        height,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0],
        events: {
          load: function () {
            highlightSection(this);
          },
          redraw: function () {
            highlightSection(this);
          },
        },
      },
      colors: chartColors || [],
      tooltip: {
        formatter: () => false,
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: "pointer",
          borderWidth: 0,
          borderRadius: 0,
          startAngle: -90,
          endAngle: 90,
          size: "100%",
          innerSize: "55%",
          dataLabels: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: true,
            },
            inactive: {
              enabled: false,
            },
          },
          point: {
            events: {
              mouseOver: function () {
                if (setActiveItem) {
                  setActiveItem(this.name);
                }
              },
              mouseOut: function () {
                if (setActiveItem) {
                  setActiveItem(undefined);
                }
              },
            },
          },
        },
      },
      series: [
        {
          colorByPoint: true,
          data: chartData,
          type: "pie",
        },
      ],
    };
  }, [width, height, chartColors, chartData, setActiveItem, highlightSection]);
};
